import type { ElementType } from 'react';
import {
  Calendar,
  CalendarCheck,
  CreditCard,
  CalendarClock,
  ClipboardList,
  FileInput,
  FileSignature,
  FileText,
  Gauge,
  HeartHandshake,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  NotebookPen,
  Settings,
  UserCog,
  ShieldAlert,
  SlidersHorizontal,
  Users,
  UsersRound,
} from 'lucide-react';
import type { ShellNavSection } from '../shell/types';

/** Sidebar for the PRP (Psychiatric Rehabilitation Program) workspace. */
export const PRP_NAV: ShellNavSection[] = [
  {
    label: 'My Work',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, href: '/prp' },
      { label: 'Caseload', icon: Users, href: '/prp/caseload' },
      { label: 'Documents', icon: FileText, href: '/prp/documents' },
      { label: 'Contact Notes', icon: NotebookPen, href: '/prp/contact-notes' },
    ],
  },
  {
    label: 'Inbox',
    items: [
      { label: 'My Tasks', icon: ListChecks, href: '/prp/tasks' },
      { label: 'Messages', icon: Mail, href: '/prp/messages' },
    ],
  },
  {
    label: 'Intake',
    items: [
      { label: 'Referrals', icon: FileInput, href: '/prp/referrals' },
      { label: 'Authorizations', icon: CalendarClock, href: '/prp/authorizations' },
    ],
  },
  {
    label: 'Planning',
    items: [
      { label: 'IRPs', icon: ClipboardList, href: '/prp/irps' },
      { label: 'DLA-20', icon: Gauge, href: '/prp/dla20' },
      { label: 'Monthly Summaries', icon: FileText, href: '/prp/monthly-summaries' },
    ],
  },
  {
    label: 'Oversight',
    items: [
      { label: 'Pending Sign-offs', icon: FileSignature, href: '/prp/reviews' },
      { label: 'Compliance', icon: ShieldAlert, href: '/prp/compliance' },
      { label: 'Program Settings', icon: SlidersHorizontal, href: '/prp/settings' },
    ],
  },
];

/**
 * The PRP sidebar is the whole app for PRP staff and PRP-only clinics, so it
 * also carries the shared tools: Calendar for anyone allowed to use it, and
 * Clinic (users, locations, billing, settings) for a PRP-only clinic's admin.
 */
export function prpWorkspaceNav(opts: {
  prpOnlyOrg: boolean;
  admin: boolean;
  can: (resource: string, action: string) => boolean;
}): ShellNavSection[] {
  const calendar: ShellNavSection['items'] = [];
  if (opts.admin || opts.can('appointments', 'list')) {
    calendar.push(
      { label: 'My Day', icon: CalendarCheck, href: '/my-day' },
      { label: 'Schedule', icon: Calendar, href: '/schedule' },
    );
  }
  if (opts.admin || opts.can('groups', 'list')) {
    calendar.push({ label: 'Groups', icon: UsersRound, href: '/groups' });
  }
  // Calendar right after My Work.
  const [myWork, ...rest] = PRP_NAV;
  const sections: ShellNavSection[] = myWork ? [myWork] : [];
  if (calendar.length) sections.push({ label: 'Calendar', items: calendar });
  sections.push(...rest);
  if (opts.prpOnlyOrg && opts.admin) {
    sections.push({
      label: 'Clinic',
      items: [
        { label: 'Users', icon: UserCog, href: '/settings/users' },
        { label: 'Locations', icon: MapPin, href: '/settings/locations' },
        { label: 'Billing', icon: CreditCard, href: '/billing' },
        { label: 'All Settings', icon: Settings, href: '/settings' },
      ],
    });
  }
  return sections;
}

export const PRP_SIDEBAR_HEADER = {
  title: 'PRP',
  subtitle: 'Psychiatric Rehab Program',
  icon: HeartHandshake as ElementType,
};

/** Main-app sidebar entry that opens the PRP workspace. */
export const PRP_ENTRY_ITEM = {
  label: 'PRP Workspace',
  icon: HeartHandshake as ElementType,
  href: '/prp',
};
